import { CommentDialog } from '../../utils/CommentDialog';
import { MaterialDetailsDialog } from '../../utils/MaterialDetailsDialog';
import ConfirmMarkDoneDialog from './ConfirmMarkDoneDialog';
import ConfirmSkipStepDialog from './ConfirmSkipStepDialog';
import FullScreenActionLoader from '../../components/common/FullScreenActionLoader';
import { DASH_PLACEHOLDER } from '../../utils/missingValue';
import type { ProcessingStep, SPLotInfo } from './types';

type Props = {
    commentDialogOpen: boolean;
    setCommentDialogOpen: (v: boolean) => void;
    handleSaveComment: () => void;
    commentText: string;
    setCommentText: (t: string) => void;
    currentStepForComment: number | null;
    originalCommentText: string;
    processingSteps: ProcessingStep[];
    materialDialogOpen: boolean;
    setMaterialDialogOpen: (v: boolean) => void;
    spLotsDialogOpen: boolean;
    setSpLotsDialogOpen: (v: boolean) => void;
    confirmDialogOpen: boolean;
    setConfirmDialogOpen: (v: boolean) => void;
    skipDialogOpen?: boolean;
    setSkipDialogOpen?: (v: boolean) => void;
    skipComment?: string;
    setSkipComment?: (v: string) => void;
    handleConfirmSkipStep?: () => Promise<void>;
    skipStepName?: string;
    spLotIds: string[];
    spLotInfo: SPLotInfo;
    handleConfirmMarkAsDone: () => Promise<void>;
    isActionInProgress: boolean;
    classes: any;
};

const ProcessingWorkflowDialogs = ({ commentDialogOpen, setCommentDialogOpen, handleSaveComment, commentText, setCommentText, currentStepForComment, originalCommentText, processingSteps, materialDialogOpen, setMaterialDialogOpen, spLotsDialogOpen, setSpLotsDialogOpen, confirmDialogOpen, setConfirmDialogOpen, skipDialogOpen, setSkipDialogOpen, skipComment, setSkipComment, handleConfirmSkipStep, skipStepName, spLotIds, spLotInfo, handleConfirmMarkAsDone, isActionInProgress, classes }: Props) => {
    return (
        <>
            <CommentDialog
                open={commentDialogOpen}
                onClose={() => setCommentDialogOpen(false)}
                onSave={handleSaveComment}
                commentText={commentText}
                onCommentTextChange={setCommentText}
                stepName={currentStepForComment ? processingSteps.find(s => s.id === currentStepForComment)?.name || '' : ''}
                originalCommentText={originalCommentText}
            />

            <MaterialDetailsDialog
                open={materialDialogOpen}
                onClose={() => setMaterialDialogOpen(false)}
                mode="materials"
                materials={spLotInfo?.materials.map(mat => {
                    const priorityLower = mat.processing_priority?.toLowerCase();
                    let priority: 'High' | 'Medium' | 'Low' | 'NA' = 'NA';
                    if (priorityLower === 'high') priority = 'High';
                    else if (priorityLower === 'medium') priority = 'Medium';
                    else if (priorityLower === 'low') priority = 'Low';
                    return {
                        id: mat.matid || DASH_PLACEHOLDER,
                        seedWeight: mat.fruit_weight !== undefined && mat.fruit_weight !== null ? mat.fruit_weight.toString() : DASH_PLACEHOLDER,
                        seedWeightUnit: 'g',
                        seedCount: mat.fruit_count !== undefined && mat.fruit_count !== null ? mat.fruit_count : 0,
                        priority,
                    };
                }) || []}
                spLotId={spLotIds[0] || ''}
            />

            <MaterialDetailsDialog
                open={spLotsDialogOpen}
                onClose={() => setSpLotsDialogOpen(false)}
                mode="splots"
                spLotIds={spLotIds}
            />

            <ConfirmMarkDoneDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleConfirmMarkAsDone}
                processingSteps={processingSteps}
                isActionInProgress={isActionInProgress}
                classes={classes}
            />

            {handleConfirmSkipStep && (
                <ConfirmSkipStepDialog
                    open={Boolean(skipDialogOpen)}
                    onClose={() => setSkipDialogOpen?.(false)}
                    onConfirm={handleConfirmSkipStep}
                    stepName={skipStepName}
                    comment={skipComment ?? ''}
                    onCommentChange={(value) => setSkipComment?.(value)}
                    isActionInProgress={isActionInProgress}
                    classes={classes}
                />
            )}

            <FullScreenActionLoader open={isActionInProgress} />
        </>
    );
};

export default ProcessingWorkflowDialogs;
